import { useClient } from '@cortex/react-query';
import type { ModelConfig } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import { PageHeader } from '../../components/common/PageHeader';
import { Table, type IColumn } from '../../components/common/Table';
import { routes } from '../../lib/constants';
import { formatRelative } from '../../lib/format';

export const Route = createFileRoute('/models/')({ component: ModelsPage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function ModelsPage() {
  const client = useClient();
  const nav = useNavigate();
  const list = useQuery({
    queryKey: ['model-configs'],
    queryFn: () => client.http.get('v1/model-configs').json<IListEnvelope<ModelConfig>>(),
  });

  const cols: IColumn<ModelConfig>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (m) => (
        <Link
          to={routes.modelDetail(m.id)}
          style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}
        >
          {m.name}
        </Link>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      cell: (m) => <span className="badge badge--tool">{m.provider}</span>,
      width: 140,
    },
    { key: 'modelId', header: 'Model', mono: true, cell: (m) => m.modelId },
    {
      key: 'endpoint',
      header: 'Endpoint',
      mono: true,
      cell: (m) =>
        m.endpoint ? (
          <span className="t-secondary">{m.endpoint}</span>
        ) : (
          <span className="t-muted">—</span>
        ),
    },
    {
      key: 'updated',
      header: 'Updated',
      mono: true,
      cell: (m) => formatRelative(m.updatedAt),
      width: 140,
    },
  ];

  return (
    <>
      <PageHeader
        title="Model configs"
        actions={
          <Link to={routes.MODELS_NEW} className="btn btn--primary btn--sm">
            New model config
          </Link>
        }
      />
      <Table<ModelConfig>
        rows={list.data?.items}
        columns={cols}
        rowKey={(m) => m.id}
        isLoading={list.isLoading}
        error={list.error as Error | null}
        emptyTitle="No model configs yet"
        emptyDescription="Configure at least one provider before agents can run."
        emptyCta={
          <Link to={routes.MODELS_NEW} className="btn btn--primary btn--sm">
            New model config
          </Link>
        }
        onRowClick={async (m) => {
          try {
            await nav({ to: routes.modelDetail(m.id) });
          } catch (err) {
            console.error(err);
          }
        }}
      />
    </>
  );
}
